import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ChartBar, Users, TrendUp, CurrencyDollar, Sparkle, Globe, Bell, Star, Gavel, Lightning, ArrowLeft } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function AdminDashboardPage() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [dash, setDash] = useState(null);
  const [usersList, setUsersList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [genCountry, setGenCountry] = useState('Nigeria');
  const [genCategory, setGenCategory] = useState('general');
  const [genNotify, setGenNotify] = useState(true);
  const [genFeatured, setGenFeatured] = useState(false);
  const [tab, setTab] = useState('overview');
  const [countries, setCountries] = useState({});

  const fetchDash = useCallback(async () => {
    try {
      const [d, u, c] = await Promise.all([
        api.get('/admin/dashboard'),
        api.get('/admin/users'),
        api.get('/countries')
      ]);
      setDash(d.data);
      setUsersList(u.data);
      setCountries(c.data);
    } catch (err) {
      if (err.response?.status === 403) navigate('/markets');
    } finally { setLoading(false); }
  }, [api, navigate]);

  useEffect(() => { fetchDash(); }, [fetchDash]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post('/admin/markets/generate-and-push', {
        country: genCountry, category: genCategory, notify_users: genNotify, featured: genFeatured
      });
      toast.success(`${data.markets.length} markets created! ${genNotify ? 'Notifications sent (simulated email + SMS).' : ''}`);
      fetchDash();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generation failed');
    } finally { setGenerating(false); }
  };

  const handleFeature = async (marketId) => {
    try {
      const { data } = await api.post(`/admin/markets/${marketId}/feature`);
      toast.success(data.featured ? 'Market featured!' : 'Market unfeatured');
      fetchDash();
    } catch { toast.error('Failed to toggle feature'); }
  };

  const handleResolve = async (marketId, resolution) => {
    if (!window.confirm(`Resolve as ${resolution.toUpperCase()}? This will process payouts.`)) return;
    try {
      const { data } = await api.post(`/admin/markets/${marketId}/resolve`, { resolution });
      toast.success(`Resolved ${resolution.toUpperCase()}: ${data.winners} winners, ${data.losers} losers`);
      fetchDash();
    } catch (err) { toast.error(err.response?.data?.detail || 'Resolve failed'); }
  };

  if (loading) return (
    <div className="min-h-screen pt-24 flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-[#00FFA3] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!dash) return null;
  const allCountries = Object.values(countries).flat();

  const TABS = [
    { key: 'overview', label: 'Overview', icon: ChartBar },
    { key: 'generate', label: 'AI Generate', icon: Sparkle },
    { key: 'markets', label: 'Markets', icon: TrendUp },
    { key: 'users', label: 'Users', icon: Users },
  ];

  return (
    <div className="min-h-screen pt-20 px-4 md:px-8 pb-12" style={{ backgroundColor: 'var(--oh-bg)' }} data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto">
        <button onClick={() => navigate('/markets')} className="flex items-center gap-2 mb-4 mt-4" style={{ color: 'var(--oh-text-secondary)' }}>
          <ArrowLeft size={18} /> Back to Markets
        </button>
        <div className="flex items-center gap-3 mb-6">
          <h1 className="font-heading text-2xl sm:text-3xl" style={{ color: 'var(--oh-text)' }}>Admin Dashboard</h1>
          <span className="bg-[rgba(245,158,11,0.1)] text-[#F59E0B] text-xs px-2 py-0.5 rounded-full border border-[rgba(245,158,11,0.2)]">Admin</span>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`admin-tab-${t.key}`}
              className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-all border ${
                tab === t.key ? 'bg-[rgba(0,255,163,0.1)] text-[#00FFA3] border-[rgba(0,255,163,0.3)]' : 'border-[var(--oh-border)]'
              }`} style={tab !== t.key ? { color: 'var(--oh-text-secondary)', backgroundColor: 'var(--oh-card)' } : {}}>
              <t.icon size={16} weight={tab === t.key ? 'fill' : 'regular'} /> {t.label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {tab === 'overview' && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {[
                { label: 'Users', value: dash.users.total, icon: Users, color: '#00B8FF' },
                { label: 'Active Markets', value: dash.markets.active, icon: TrendUp, color: '#00FFA3' },
                { label: 'Resolved', value: dash.markets.resolved, icon: Gavel, color: '#F59E0B' },
                { label: 'Total Trades', value: dash.trades.total, icon: Lightning, color: '#00FFA3' },
                { label: 'Total Volume', value: `$${(dash.volume.total / 1000).toFixed(0)}K`, icon: CurrencyDollar, color: '#F59E0B' },
                { label: 'Featured', value: dash.markets.featured, icon: Star, color: '#00B8FF' },
              ].map((s, i) => (
                <div key={i} className="rounded-xl p-4 border" style={{ backgroundColor: 'var(--oh-card)', borderColor: 'var(--oh-border)' }}>
                  <div className="flex items-center gap-1.5 mb-1">
                    <s.icon size={14} style={{ color: s.color }} weight="fill" />
                    <span className="text-xs uppercase tracking-wider font-bold" style={{ color: 'var(--oh-text-muted)' }}>{s.label}</span>
                  </div>
                  <p className="font-mono text-xl font-bold" style={{ color: 'var(--oh-text)' }}>{s.value}</p>
                </div>
              ))}
            </div>

            {/* Category Distribution */}
            <div className="rounded-xl p-6 border" style={{ backgroundColor: 'var(--oh-card)', borderColor: 'var(--oh-border)' }}>
              <h3 className="font-heading text-base mb-4" style={{ color: 'var(--oh-text)' }}>Category Distribution</h3>
              <div className="flex flex-wrap gap-3">
                {Object.entries(dash.category_distribution).map(([cat, count]) => (
                  <div key={cat} className="px-4 py-2 rounded-lg border text-sm" style={{ borderColor: 'var(--oh-border)', color: 'var(--oh-text)' }}>
                    <span className="capitalize">{cat}</span> <span className="font-mono text-[#00FFA3] ml-1">{count}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Daily Challenge */}
            {dash.daily_challenge && (
              <div className="rounded-xl p-6 border border-[rgba(245,158,11,0.3)]" style={{ backgroundColor: 'var(--oh-card)' }}>
                <h3 className="font-heading text-base mb-2 flex items-center gap-2" style={{ color: 'var(--oh-text)' }}>
                  <Lightning size={18} className="text-[#F59E0B]" weight="fill" /> Today's Daily Challenge
                </h3>
                <p className="text-sm" style={{ color: 'var(--oh-text-secondary)' }}>{dash.daily_challenge.market_question}</p>
              </div>
            )}
          </div>
        )}

        {/* AI Generate Tab */}
        {tab === 'generate' && (
          <div className="max-w-lg rounded-xl p-6 border" style={{ backgroundColor: 'var(--oh-card)', borderColor: 'var(--oh-border)' }}>
            <h3 className="font-heading text-lg mb-5 flex items-center gap-2" style={{ color: 'var(--oh-text)' }}>
              <Sparkle size={20} className="text-[#00FFA3]" weight="fill" /> Generate & Push Markets
            </h3>
            <div className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-wider font-bold mb-2 block" style={{ color: 'var(--oh-text-secondary)' }}>Country</label>
                <Select value={genCountry} onValueChange={setGenCountry}>
                  <SelectTrigger style={{ backgroundColor: 'var(--oh-bg)', borderColor: 'var(--oh-border)', color: 'var(--oh-text)' }} data-testid="admin-gen-country">
                    <Globe size={16} className="mr-2 text-[#00B8FF]" /><SelectValue />
                  </SelectTrigger>
                  <SelectContent style={{ backgroundColor: 'var(--oh-card)', borderColor: 'var(--oh-border)', color: 'var(--oh-text)' }}>
                    {allCountries.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-bold mb-2 block" style={{ color: 'var(--oh-text-secondary)' }}>Category</label>
                <Select value={genCategory} onValueChange={setGenCategory}>
                  <SelectTrigger style={{ backgroundColor: 'var(--oh-bg)', borderColor: 'var(--oh-border)', color: 'var(--oh-text)' }} data-testid="admin-gen-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent style={{ backgroundColor: 'var(--oh-card)', borderColor: 'var(--oh-border)', color: 'var(--oh-text)' }}>
                    {['general','economics','politics','technology','crypto','sports','climate','entertainment'].map(c => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer text-sm" style={{ color: 'var(--oh-text)' }}>
                  <input type="checkbox" checked={genNotify} onChange={e => setGenNotify(e.target.checked)} className="accent-[#00FFA3]" />
                  <Bell size={16} /> Notify all users (in-app + email + SMS)
                </label>
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer text-sm" style={{ color: 'var(--oh-text)' }}>
                  <input type="checkbox" checked={genFeatured} onChange={e => setGenFeatured(e.target.checked)} className="accent-[#F59E0B]" />
                  <Star size={16} /> Mark as Featured
                </label>
              </div>
              <Button onClick={handleGenerate} disabled={generating} data-testid="admin-generate-button"
                className="w-full bg-gradient-to-r from-[#00FFA3] to-[#00B8FF] text-black font-bold h-12 text-base">
                <Sparkle size={18} className="mr-2" weight="fill" />
                {generating ? 'Generating...' : 'Generate & Push to Users'}
              </Button>
              <p className="text-xs text-center" style={{ color: 'var(--oh-text-muted)' }}>
                AI generates 3 markets. Notifications (in-app, email, SMS) are simulated for MVP.
              </p>
            </div>
          </div>
        )}

        {/* Markets Tab */}
        {tab === 'markets' && (
          <div className="space-y-2">
            <p className="text-sm mb-3" style={{ color: 'var(--oh-text-secondary)' }}>
              {dash.markets.total} total markets. Click to manage.
            </p>
            {dash.featured_markets?.length > 0 && (
              <div className="mb-4">
                <h3 className="text-xs uppercase tracking-wider font-bold mb-2 text-[#F59E0B]">Featured Markets</h3>
                {dash.featured_markets.map(m => (
                  <div key={m.id} className="flex items-center gap-3 px-4 py-3 rounded-lg border mb-2" style={{ backgroundColor: 'var(--oh-card)', borderColor: 'rgba(245,158,11,0.3)' }}>
                    <Star size={16} className="text-[#F59E0B] flex-shrink-0" weight="fill" />
                    <span className="flex-1 text-sm truncate" style={{ color: 'var(--oh-text)' }}>{m.question}</span>
                    <Button size="sm" variant="outline" onClick={() => handleFeature(m.id)} className="text-xs border-[var(--oh-border)]">Unfeature</Button>
                    <Button size="sm" onClick={() => handleResolve(m.id, 'yes')} className="text-xs bg-[#00FFA3] text-black">YES</Button>
                    <Button size="sm" onClick={() => handleResolve(m.id, 'no')} className="text-xs bg-[#00B8FF] text-black">NO</Button>
                  </div>
                ))}
              </div>
            )}
            <p className="text-xs" style={{ color: 'var(--oh-text-muted)' }}>
              Use market detail pages to resolve individual markets. Feature markets from here or detail page.
            </p>
          </div>
        )}

        {/* Users Tab */}
        {tab === 'users' && (
          <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: 'var(--oh-card)', borderColor: 'var(--oh-border)' }}>
            <div className="hidden sm:grid grid-cols-[1fr_auto_auto_auto_auto] gap-4 px-5 py-3 border-b text-xs uppercase tracking-wider font-bold" style={{ borderColor: 'var(--oh-border)', color: 'var(--oh-text-muted)' }}>
              <span>User</span><span className="text-right">Balance</span><span className="text-right">Trades</span><span className="text-right">Role</span><span className="text-right">Joined</span>
            </div>
            {usersList.map(u => (
              <div key={u.id} className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto_auto_auto] gap-2 sm:gap-4 px-5 py-3 border-b last:border-0 items-center" style={{ borderColor: 'var(--oh-border)' }}
                data-testid={`admin-user-${u.id}`}>
                <div>
                  <p className="text-sm font-medium" style={{ color: 'var(--oh-text)' }}>{u.name}</p>
                  <p className="text-xs" style={{ color: 'var(--oh-text-muted)' }}>{u.email}</p>
                </div>
                <p className="font-mono text-sm text-right" style={{ color: 'var(--oh-text)' }}>${u.wallet_balance.toLocaleString(undefined, { maximumFractionDigits: 2 })}</p>
                <p className="font-mono text-sm text-right" style={{ color: 'var(--oh-text-secondary)' }}>{u.trade_count}</p>
                <p className="text-xs text-right">
                  <span className={u.role === 'admin' ? 'text-[#F59E0B]' : 'text-[var(--oh-text-muted)]'}>{u.role}</span>
                </p>
                <p className="text-xs text-right" style={{ color: 'var(--oh-text-muted)' }}>{u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
